import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { ConfirmModalRef } from '@shared/components/ui/ConfirmModal/ConfirmModal';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useToast } from '@shared/hooks/use-toast';
import { resolveApiAssetUrl } from '@shared/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { useContalinkInvoices } from '../../hooks/useContalinkInvoices';
import { useHoldedInvoices } from '../../hooks/useHoldedInvoices';
import { useInvoicesMain } from '../../hooks/useInvoicesMain';
import { Invoice } from '../../models/Invoices';
import { isPendingContalinkInvoice } from '../../utils/invoiceAccounting';
import { InvoicesAgGridTable } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection, InvoicesTopBarActions } from '../interaction';
import {
  AddManualInvoiceModal,
  ContalinkInvoiceModal,
  ContalinkPendingInvoicesModal,
  CreateHoldedInvoiceModal,
  HoldedInvoicesModal,
  type AddManualInvoiceModalRef,
  type ContalinkInvoiceModalRef,
  type CreateHoldedInvoiceModalRef,
} from '../modals';

export const Main = () => {
  const { t } = useTranslation('invoicesModule');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getTeamType, isHoldedAccounting, isContalinkAccounting } = usePermissions();
  const isTenant3PL = getTeamType() === 'tenant_3pl';
  const showHoldedActions = isTenant3PL && isHoldedAccounting();
  const showContalinkActions = isTenant3PL && isContalinkAccounting();

  // 🎯 Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 🎯 Hook principal para obtener facturas y controlar filtros
  const {
    invoices,
    isLoading,
    isFetching,
    error,
    total,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasActiveFilters,
    filterConditions,
    searchTerm,
    selectedColumns,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    sorting,
    handleSortingChange,
  } = useInvoicesMain();

  const handleViewInvoice = useCallback(
    (invoice: Invoice) => {
      if (!invoice?.id) {
        return;
      }

      navigate(`/invoices/view/${invoice.id}`);
    },
    [navigate],
  );

  const handleDownloadInvoice = useCallback(
    (invoice: Invoice) => {
      const pdfHref = resolveApiAssetUrl(invoice.pdf_url);
      if (!pdfHref) {
        toast({
          title: t('toast.download.error.title', 'Archivo no disponible'),
          description: t('toast.download.error.description', 'Esta factura no cuenta con un PDF disponible.'),
          variant: 'destructive',
        });
        return;
      }

      if (typeof document === 'undefined') {
        return;
      }

      window.open(pdfHref, '_blank', 'noopener');
    },
    [toast, t],
  );

  const contalinkModalRef = useRef<ContalinkInvoiceModalRef>(null);

  const openContalinkForm = useCallback(
    (invoice: Invoice) => {
      contalinkModalRef.current?.open(invoice, () => refetch());
    },
    [refetch],
  );

  const handleRegisterContalink = useCallback(
    (invoice: Invoice) => {
      if (!isPendingContalinkInvoice(invoice)) {
        toast({
          title: t('toast.contalink.invalid_invoice.title', 'Factura no válida'),
          description: t(
            'toast.contalink.invalid_invoice.description',
            'Solo se pueden registrar facturas sin erp_id ni erp_number',
          ),
          variant: 'destructive',
        });
        return;
      }

      openContalinkForm(invoice);
    },
    [openContalinkForm, toast, t],
  );

  const {
    showPendingInvoices: showContalinkPendingInvoices,
    isLoadingPending: isLoadingContalinkPending,
    modalRef: contalinkPendingModalRef,
  } = useContalinkInvoices();

  const handleRegisterContalinkFromHeader = useCallback(() => {
    showContalinkPendingInvoices(openContalinkForm);
  }, [showContalinkPendingInvoices, openContalinkForm]);

  const { filteredColumnDefs, tanStackColumns } = useAgGridColumns({
    selectedColumns,
    onViewInvoice: handleViewInvoice,
    onDownloadInvoice: handleDownloadInvoice,
    onRegisterContalink: handleRegisterContalink,
  });

  // 🎯 Hook para integración con Holded
  const {
    showPendingInvoices,
    showPendingConfirmInvoices,
    isGenerating,
    isConfirming,
    isLoadingPending,
    isLoadingPendingConfirm,
    modalRef: holdedModalRef,
  } = useHoldedInvoices();
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const manualInvoiceModalRef = useRef<AddManualInvoiceModalRef>(null);
  const createHoldedInvoiceModalRef = useRef<CreateHoldedInvoiceModalRef>(null);

  const handleGenerateHolded = useCallback(() => {
    showPendingInvoices(() => refetch());
  }, [showPendingInvoices, refetch]);

  const handleConfirmHolded = useCallback(() => {
    showPendingConfirmInvoices(() => refetch());
  }, [showPendingConfirmInvoices, refetch]);

  const handleCreateManualInvoice = useCallback(() => {
    manualInvoiceModalRef.current?.open(() => refetch());
  }, [refetch]);

  const handleCreateHoldedInvoice = useCallback(() => {
    createHoldedInvoiceModalRef.current?.open(() => refetch());
  }, [refetch]);

  // 🎯 Configurar breadcrumbs
  const breadcrumbs = useMemo(
    () => [{ label: t('breadcrumb.root', 'Facturación') }, { label: t('breadcrumb.invoices', 'Facturas') }],
    [t],
  );

  // 🎯 Acciones del header: dos desplegables (crear + Holded) para tenant_3pl
  const headerActions = useMemo(() => {
    if (!isTenant3PL) {
      return [];
    }

    return [
      {
        id: 'invoices-topbar-actions',
        label: '',
        onClick: () => {},
        component: (
          <InvoicesTopBarActions
            showHoldedActions={showHoldedActions}
            showContalinkActions={showContalinkActions}
            onCreateHoldedInvoice={handleCreateHoldedInvoice}
            onCreateManualInvoice={handleCreateManualInvoice}
            onGenerateHolded={handleGenerateHolded}
            onConfirmHolded={handleConfirmHolded}
            onRegisterContalink={handleRegisterContalinkFromHeader}
            isLoadingPending={isLoadingPending}
            isLoadingPendingConfirm={isLoadingPendingConfirm}
            isLoadingContalinkPending={isLoadingContalinkPending}
            isGenerating={isGenerating}
            isConfirming={isConfirming}
          />
        ),
      },
    ];
  }, [
    isTenant3PL,
    showHoldedActions,
    showContalinkActions,
    handleCreateHoldedInvoice,
    handleCreateManualInvoice,
    handleGenerateHolded,
    handleConfirmHolded,
    handleRegisterContalinkFromHeader,
    isGenerating,
    isConfirming,
    isLoadingPending,
    isLoadingPendingConfirm,
    isLoadingContalinkPending,
  ]);

  // 🎯 Establecer breadcrumbs y acciones en la topbar
  usePageNavigation({
    breadcrumbs,
    headerActions,
  });

  // 🎯 Efecto para desactivar el spinner de carga inicial después del primer fetch
  useEffect(() => {
    // Si isLoading pasa de true a false, significa que la carga inicial terminó.
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad) {
    return <LoadingState message={t('loading.message', 'Cargando facturas...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* Header con filtros */}
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              handleSearch={handleSearch}
              onFilterChange={handleFilterChange}
              columns={tanStackColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              filterConditions={filterConditions}
              searchTerm={searchTerm}
            />
          </div>
          <TotalResults total={total} />
        </div>
      </div>

      {/* Estado vacío */}
      {!isLoading && invoices.length === 0 && (
        <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.ORDERS} />
      )}

      {/* Tabla principal */}
      {!isLoading && invoices.length > 0 && (
        <div
          className={`transition-opacity duration-300 ${
            isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'
          }`}
        >
          <InvoicesAgGridTable
            invoices={invoices}
            columnDefs={filteredColumnDefs}
            isLoading={isLoading}
            isFetching={isFetching}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            sorting={sorting}
            onSortingChange={handleSortingChange}
          />
        </div>
      )}

      {/* Modal de confirmación */}
      <ConfirmModal ref={confirmModalRef} />

      {showHoldedActions && (
        <>
          <HoldedInvoicesModal ref={holdedModalRef} onConfirm={() => refetch()} />
          <CreateHoldedInvoiceModal ref={createHoldedInvoiceModalRef} />
        </>
      )}
      <AddManualInvoiceModal ref={manualInvoiceModalRef} />
      {showContalinkActions && <ContalinkPendingInvoicesModal ref={contalinkPendingModalRef} />}
      <ContalinkInvoiceModal ref={contalinkModalRef} />
    </div>
  );
};
